import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import {
  tokens,
  formatIcd10,
  PatientStrip,
  SectionPanel,
  StatusPill,
  CodeChip,
  ClinicalEmptyState,
} from "../../shared/clinical-design";
import { PREP_STATUS, PREP_STATUS_META, GAP_PRIORITY_TONE } from "../common/constants";

const labelSx = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: tokens.ink3,
};

const ItemRow = ({ children }) => (
  <Stack
    direction="row"
    alignItems="flex-start"
    spacing={1}
    sx={{ py: 0.6, borderBottom: `1px solid ${tokens.line}`, "&:last-of-type": { border: 0 } }}
  >
    {children}
  </Stack>
);

/**
 * Panel de detalle del prep packet (huddle sheet) de una cita:
 * patient strip + HCC sospechosos + care gaps + meds + labs + agenda sugerida.
 *
 * @param {Object} appointment - cita seleccionada (shape de fakeData)
 * @param {Function} onMarkReviewed - (appointmentId) => void
 * @param {boolean} reviewSaving
 */
export default function PrepPacketDetail({ appointment, onMarkReviewed, reviewSaving = false }) {
  if (!appointment) {
    return (
      <SectionPanel title="Prep packet">
        <ClinicalEmptyState message="Select an appointment from the queue to open its pre-visit prep packet." />
      </SectionPanel>
    );
  }

  const statusMeta = PREP_STATUS_META[appointment.status];

  return (
    <Stack spacing={1.5}>
      <PatientStrip
        patient={appointment.patient}
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <StatusPill tone={statusMeta.tone} label={statusMeta.label} dense />
            {appointment.status !== PREP_STATUS.REVIEWED && (
              <Button
                size="small"
                variant="outlined"
                disabled={reviewSaving}
                onClick={() => onMarkReviewed(appointment.id)}
                sx={{
                  textTransform: "none",
                  fontSize: 12.5,
                  color: tokens.primary,
                  borderColor: tokens.primary,
                  "&:hover": { borderColor: tokens.primary700, bgcolor: tokens.primary50 },
                }}
              >
                {reviewSaving ? "Saving..." : "Mark reviewed"}
              </Button>
            )}
          </Stack>
        }
      />

      <SectionPanel
        title={`Suspected HCC conditions to address (${appointment.suspectedHcc.length})`}
        contentSx={{ pt: 1, pb: 1 }}
      >
        {appointment.suspectedHcc.length === 0 && (
          <ClinicalEmptyState message="No suspected HCC conditions for this visit." sx={{ py: 1 }} />
        )}
        {appointment.suspectedHcc.map((c) => (
          <ItemRow key={c.icd}>
            <CodeChip code={formatIcd10(c.icd)} raw={c.icd} tone="warn" />
            {c.hcc !== "—" && <CodeChip code={c.hcc} />}
            <Typography sx={{ fontSize: 13, color: tokens.ink, flex: 1 }}>
              {c.description}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: tokens.ink3, whiteSpace: "nowrap" }}>
              {c.source}
            </Typography>
          </ItemRow>
        ))}
      </SectionPanel>

      <SectionPanel title={`Open care gaps (${appointment.careGaps.length})`} contentSx={{ pt: 1, pb: 1 }}>
        {appointment.careGaps.length === 0 && (
          <ClinicalEmptyState message="No open care gaps." sx={{ py: 1 }} />
        )}
        {appointment.careGaps.map((g) => (
          <ItemRow key={g.measure}>
            <CodeChip code={g.measure} />
            <Typography sx={{ fontSize: 13, color: tokens.ink, flex: 1 }}>
              {g.description}
            </Typography>
            <StatusPill tone={GAP_PRIORITY_TONE[g.priority] || "muted"} label={g.due} dense />
          </ItemRow>
        ))}
      </SectionPanel>

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        <SectionPanel
          title={`Meds to reconcile (${appointment.medsToReconcile.length})`}
          sx={{ flex: "1 1 320px", minWidth: 280 }}
          contentSx={{ pt: 1, pb: 1 }}
        >
          {appointment.medsToReconcile.length === 0 && (
            <ClinicalEmptyState message="No medication issues flagged." sx={{ py: 1 }} />
          )}
          {appointment.medsToReconcile.map((m) => (
            <Box key={m.name} sx={{ py: 0.6, borderBottom: `1px solid ${tokens.line}`, "&:last-of-type": { border: 0 } }}>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: tokens.ink }}>
                {m.name}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: tokens.warn }}>{m.issue}</Typography>
            </Box>
          ))}
        </SectionPanel>

        <SectionPanel
          title={`Last labs (${appointment.lastLabs.length})`}
          sx={{ flex: "1 1 320px", minWidth: 280 }}
          contentSx={{ pt: 1, pb: 1 }}
        >
          {appointment.lastLabs.length === 0 && (
            <ClinicalEmptyState message="No recent labs on file." sx={{ py: 1 }} />
          )}
          {appointment.lastLabs.map((l) => (
            <ItemRow key={l.test}>
              <Typography sx={{ fontSize: 13, color: tokens.ink, flex: 1 }}>{l.test}</Typography>
              <Typography
                sx={{
                  fontFamily: tokens.fontMono,
                  fontVariantNumeric: "tabular-nums",
                  fontSize: 13,
                  fontWeight: 600,
                  color:
                    l.flag === "crit" ? tokens.crit : l.flag === "warn" ? tokens.warn : tokens.ink,
                }}
              >
                {l.value}
              </Typography>
              <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 12, color: tokens.ink3 }}>
                {l.date}
              </Typography>
            </ItemRow>
          ))}
        </SectionPanel>
      </Box>

      <SectionPanel title="Suggested visit agenda" contentSx={{ pt: 1, pb: 1 }}>
        {appointment.agendaItems.length === 0 && (
          <ClinicalEmptyState message="No agenda items suggested." sx={{ py: 1 }} />
        )}
        {appointment.agendaItems.map((item, i) => (
          <Stack key={item} direction="row" spacing={1} sx={{ py: 0.5 }}>
            <Typography
              sx={{
                fontFamily: tokens.fontMono,
                fontSize: 12.5,
                color: tokens.primary,
                minWidth: 20,
              }}
            >
              {i + 1}.
            </Typography>
            <Typography sx={{ fontSize: 13, color: tokens.ink }}>{item}</Typography>
          </Stack>
        ))}
      </SectionPanel>

      <Typography sx={labelSx}>
        Visit: {appointment.visitType} · {appointment.date} {appointment.time}
      </Typography>
    </Stack>
  );
}
